import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Download, Tag, X } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const EMPTY = { name: "", mobile: "", whatsapp: "", email: "", birthday: "", city: "", gender: "", tags: [], consent: { marketing: false, whatsapp: false, sms: false, email: false } };

function CustomerModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial, tags: (initial?.tags || []).join(", "), consent: { ...EMPTY.consent, ...(initial?.consent || {}) } });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (initial?.id) await api.put(`/admin/crm/customers/${initial.id}`, payload);
      else await api.post("/admin/crm/customers", payload);
      toast.success("Customer saved");
      onSaved(); onClose();
    } catch (err) { toast.error(err.friendlyMessage); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="crm-modal">
      <form onSubmit={save} className="bg-white w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-xl">{initial?.id ? "Edit" : "Add"} Customer</p>
          <button type="button" onClick={onClose} aria-label="Close"><X size={20} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[["name", "Name *"], ["mobile", "Mobile Number"], ["whatsapp", "WhatsApp Number"], ["email", "Email"], ["birthday", "Birthday (DD-MM-YYYY)"], ["city", "City"], ["gender", "Gender (optional)"], ["tags", "Tags (comma separated)"]].map(([k, l]) => (
            <div key={k} className={k === "tags" ? "sm:col-span-2" : ""}>
              <label className="tq-label !text-[10px]">{l}</label>
              <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`crm-field-${k}`} className="tq-input !py-2 !text-sm" />
            </div>
          ))}
        </div>
        <p className="tq-label !text-[10px] mt-4 mb-2">Consents (never send marketing without these)</p>
        <div className="flex flex-wrap gap-4 mb-5">
          {[["marketing", "Marketing"], ["whatsapp", "WhatsApp"], ["sms", "SMS"], ["email", "Email"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input type="checkbox" checked={f.consent[k]} onChange={(e) => setF({ ...f, consent: { ...f.consent, [k]: e.target.checked } })} data-testid={`crm-consent-${k}`} className="accent-[#D4AF37] w-4 h-4" />{l}
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          <button disabled={busy} data-testid="crm-save" className="tq-btn-dark flex-1">{busy ? "Saving…" : "Save Customer"}</button>
          <button type="button" onClick={onClose} className="tq-btn-outline">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function AdminCrm() {
  const [data, setData] = useState(null);
  const [tags, setTags] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/crm/customers", { params: { q, tag, page, limit: 15 } }).then(({ data }) => setData(data)).catch((e) => toast.error(e.friendlyMessage));
    api.get("/admin/crm/tags").then(({ data }) => setTags(data.tags));
  };
  useEffect(() => { load(); }, [q, tag, page]);

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/crm/import", fd, { timeout: 120000 });
      setImportResult(data);
      toast.success(`Import complete: ${data.added} added, ${data.updated} duplicates merged`);
      load();
    } catch (err) { toast.error(err.friendlyMessage); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doExport = () => api.get("/admin/crm/export", { params: { tag }, responseType: "blob" }).then((r) => {
    const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "tarishq-customers.csv"; a.click();
  });

  const del = async (c) => { if (!window.confirm(`Delete ${c.name || c.mobile}?`)) return; await api.delete(`/admin/crm/customers/${c.id}`); toast.success("Deleted"); load(); };
  const bulkTag = async () => {
    const t = window.prompt("Tag to add to selected customers (e.g. VIP Customers):");
    if (!t) return;
    await api.post("/admin/crm/customers/bulk-tag", { ids: selected, tag: t });
    toast.success("Tag applied"); setSelected([]); load();
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} customer(s)?`)) return;
    await api.post("/admin/crm/customers/bulk-delete", { ids: selected });
    toast.success("Deleted"); setSelected([]); load();
  };

  const ids = data?.items.map((c) => c.id) || [];
  const allSelected = ids.length > 0 && selected.length === ids.length;

  return (
    <div data-testid="admin-crm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-3xl">Customer Database</h1>
          <p className="text-sm text-[#6E6D6A] mt-1">Offline customer records with consent tracking — import Excel/CSV, dedupe by mobile/email, tag & segment for campaigns.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="crm-import-btn" className="tq-btn-dark !py-2.5"><Upload size={14} /> {busy ? "Importing…" : "Import Excel/CSV"}</button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={doImport} data-testid="crm-import-input" />
          <button onClick={doExport} data-testid="crm-export-btn" className="tq-btn-outline !py-2.5"><Download size={14} /> Export</button>
          <button onClick={() => setEditing({})} data-testid="crm-add-btn" className="tq-btn-outline !py-2.5"><Plus size={14} /> Add</button>
        </div>
      </div>

      {importResult && (
        <div className="bg-white border border-[#C5A059]/50 p-4 mb-5 text-sm" data-testid="crm-import-result">
          <div className="flex justify-between"><p className="font-semibold">Import report — {importResult.total_rows} rows</p><button onClick={() => setImportResult(null)} aria-label="Dismiss"><X size={16} /></button></div>
          <p className="text-xs text-[#6E6D6A] mt-1">{importResult.added} new · {importResult.updated} duplicates auto-merged · {importResult.errors.length} row errors</p>
          {importResult.errors.slice(0, 5).map((e, i) => <p key={i} className="text-[11px] text-[#8B1E21] mt-1">Row {e.row}: {e.error}</p>)}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name / mobile / email…" data-testid="crm-search" className="tq-input !py-2.5 max-w-xs" />
        <select value={tag} onChange={(e) => { setTag(e.target.value); setPage(1); }} data-testid="crm-tag-filter" className="tq-input !py-2.5 !w-auto bg-white">
          <option value="">All groups</option>
          {tags.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {selected.length > 0 && (
          <div className="flex gap-2 items-center bg-[#121212] text-white px-3 py-1.5" data-testid="crm-bulk-bar">
            <span className="text-xs">{selected.length} selected</span>
            <button onClick={bulkTag} data-testid="crm-bulk-tag" className="text-xs flex items-center gap-1 hover:text-[#D4AF37]"><Tag size={12} /> Tag</button>
            <button onClick={bulkDelete} data-testid="crm-bulk-delete" className="text-xs flex items-center gap-1 hover:text-[#8B1E21]"><Trash2 size={12} /> Delete</button>
            <button onClick={() => setSelected([])} aria-label="Clear selection"><X size={14} /></button>
          </div>
        )}
      </div>

      {!data ? <div className="h-48 bg-white animate-pulse" /> : data.items.length === 0 ? (
        <div className="bg-white border border-[#E5DEC9] p-12 text-center text-sm text-[#9a938a]" data-testid="crm-empty">No customers yet — import your Excel/CSV file to get started.</div>
      ) : (
        <div className="bg-white border border-[#E5DEC9] overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#8C7A5E] border-b border-[#E5DEC9]">
              <th className="p-3 w-8"><input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? [] : ids)} data-testid="crm-select-all" className="accent-[#D4AF37] w-4 h-4" aria-label="Select all" /></th>
              <th className="p-3">Customer</th><th className="p-3">Contact</th><th className="p-3">City</th><th className="p-3">Tags</th><th className="p-3">Consents</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} className="border-b border-[#F4EFE6] hover:bg-[#FAF8F5]" data-testid={`crm-row-${c.id}`}>
                  <td className="p-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => setSelected(selected.includes(c.id) ? selected.filter((x) => x !== c.id) : [...selected, c.id])} data-testid={`crm-check-${c.id}`} className="accent-[#D4AF37] w-4 h-4" aria-label="Select customer" /></td>
                  <td className="p-3"><p className="font-medium">{c.name || "—"}</p><p className="text-[11px] text-[#9a938a]">{c.birthday ? `🎂 ${c.birthday}` : ""} {c.gender}</p></td>
                  <td className="p-3 text-xs"><p>{c.mobile}</p><p className="text-[#9a938a]">{c.email}</p></td>
                  <td className="p-3 text-xs">{c.city}</td>
                  <td className="p-3">{c.tags.map((t) => <span key={t} className="inline-block text-[9px] uppercase tracking-wider font-bold bg-[#D4AF37]/15 text-[#8C7A5E] px-1.5 py-0.5 mr-1 mb-1">{t}</span>)}</td>
                  <td className="p-3 text-[10px] uppercase tracking-wider">{["marketing", "whatsapp", "sms", "email"].map((k) => <span key={k} className={`mr-1.5 ${c.consent?.[k] ? "text-[#1A3C34]" : "text-[#C9C4BB]"}`} title={k}>{k.slice(0, 4)}</span>)}</td>
                  <td className="p-3"><div className="flex gap-1"><button onClick={() => setEditing(c)} aria-label="Edit" data-testid={`crm-edit-${c.id}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#D4AF37]"><Pencil size={13} /></button><button onClick={() => del(c)} aria-label="Delete" data-testid={`crm-delete-${c.id}`} className="p-1.5 border border-[#E5DEC9] hover:border-[#8B1E21] hover:text-[#8B1E21]"><Trash2 size={13} /></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.pages > 1 && (
            <div className="flex items-center justify-between p-4 text-sm">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="tq-btn-outline !py-2 !px-3 disabled:opacity-40">Prev</button>
              <span className="text-xs text-[#8C7A5E]">Page {data.page} of {data.pages} · {data.total} customers</span>
              <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="tq-btn-outline !py-2 !px-3 disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}
      {editing !== null && <CustomerModal initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
